/**
 * @quantabit/sdk-config
 * 
 * QuantaBit SDK Unified Config
 * 
 * All SDKs share this config - single point of modification for global effect:
 * - API base URL
 * - Timeout
 * - Auth configuration
 * - Debug mode
 * 
 * Usage:
 * 1. Use default configuration directly
 *    import { getConfig } from '@quantabit/sdk-config';
 *    const config = getConfig();
 * 
 * 2. Initialize config at app startup
 *    import { initConfig } from '@quantabit/sdk-config';
 *    initConfig({ apiBaseUrl: 'https://api.example.com' });
 * 
 * 3. 使用 React Hook
 *    import { useSDKConfig } from '@quantabit/sdk-config';
 *    const { config, setApiBaseUrl } = useSDKConfig();
 */

// ============ 默认配置 ============

/**
 * 默认 API 配置
 * 注意：生产环境应通过 initConfig 覆盖这些值
 */
const DEFAULT_CONFIG = {
  // === API 配置 ===
  apiBaseUrl: '/api/v1',           // API 基础路径（相对路径，由后端代理）
  apiFullUrl: (typeof process !== 'undefined' && process.env?.QBIT_API_URL) ||
    (typeof window !== 'undefined' && window.location?.origin?.includes('dev')
      ? 'https://api.dev.qbitwallet.io'
      : 'https://api.qbitwallet.io'), // 完整 API URL Fallback 品牌域名

  // === 超时配置 ===
  timeout: 30000,                   // 请求超时（毫秒）
  retryCount: 3,                    // 重试次数
  retryDelay: 1000,                 // 重试延迟（毫秒）

  // === Auth configuration ===
  tokenStorageKey: 'qbit_did_access_token',
  refreshTokenStorageKey: 'qbit_did_refresh_token',
  currentSessionKey: 'qbit_did_current',
  autoRefreshToken: true,           // 自动刷新 Token

  // === WebSocket 配置 ===
  enableWs: typeof process !== 'undefined'
    ? (process.env?.NEXT_PUBLIC_ENABLE_WS !== 'false' && process.env?.NEXT_PUBLIC_ENABLE_WS !== false)
    : true,
  wsBaseUrl: '',                     // WebSocket URL（需通过 initConfig 配置）
  wsReconnect: true,
  wsReconnectInterval: 3000,

  // === 收银台配置 ===
  checkoutUrl: (typeof process !== 'undefined' && process.env?.CHECKOUT_URL) || 'https://pay.qbitwallet.io',
  checkoutSandboxUrl: (typeof process !== 'undefined' && process.env?.CHECKOUT_SANDBOX_URL) || 'https://sandbox-pay.qbitwallet.io',

  // === 各子系统入口 URL ===
  merchantUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_MERCHANT_URL) || 'https://merchant.qbitwallet.io',
  adminUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ADMIN_URL) || 'https://admin.qbitwallet.io',
  payUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_PAY_URL) || 'https://pay.qbitwallet.io',
  walletUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_WALLET_URL) || 'https://wallet.qbitwallet.io',
  didApiUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_DID_API_URL) || 'https://did.qbitwallet.io',
  analyticsUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_URL) || 'https://analytics.mydc.space',
  analyticsApiUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_API_URL) || 'https://analytics-api.mydc.space',
  analyticsCollectorUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_COLLECTOR_URL) || 'https://analytics.mydc.space',

  // === 调试配置 ===
  debug: process.env.NODE_ENV !== 'production',
  logLevel: 'info',                 // 'debug' | 'info' | 'warn' | 'error'

  // === 环境配置 ===
  environment: process.env.NODE_ENV || 'development',

  // === QBit Chain 链配置 ===
  chainNetwork: 'mainnet',            // 当前链网络: 'mainnet' | 'testnet' | 'devnet'
  chainEndpoint: 'https://api.mainnet.qbitchain.io/',  // RPC 端点
  chainWsEndpoint: 'wss://api.mainnet.qbitchain.io/',  // WebSocket RPC 端点
  chainCommitment: 'confirmed',       // 确认级别: 'processed' | 'confirmed' | 'finalized'
  chainTimeoutMs: 30000,              // 链交互超时（毫秒）
  // 顶层浏览器/存储入口，供 explorer-sdk / ipfs-sdk 等直接读取；切换网络时由 setChainNetwork 同步 explorerUrl
  explorerUrl: 'https://explorer.mg.qbitchain.io',
  ipfsGateway: 'https://ipfs.io/ipfs',

  // === Name Service ===
  nameService: {
    tld: '.qbit',
    apiPrefix: '/ns',
    anchorNamespace: 'qbit_ns',
  },

  // === 语言配置 ===
  language: 'zh',                    // 默认语言
  fallbackLanguage: 'en',            // 回退语言
  supportedLanguages: ['zh', 'en', 'ja', 'ko'],  // Supported Languages
  languageStorageKey: 'qbit_did_language',        // localStorage key
};

function isAbsoluteUrl(value = '') {
  return /^https?:\/\//i.test(value);
}

function trimTrailingSlash(value = '') {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

function deriveApiFullUrl(apiBaseUrl) {
  if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_API_URL) {
    return trimTrailingSlash(process.env.NEXT_PUBLIC_API_URL);
  }
  if (isAbsoluteUrl(apiBaseUrl)) {
    return trimTrailingSlash(apiBaseUrl);
  }
  // 自适应判断正式域名
  if (typeof window !== 'undefined') {
    const isIcenter = window.location?.host?.includes('icenter.space') || window.location?.host?.includes('xwin.live') || window.location?.host?.includes('xwin.live');
    if (isIcenter) {
      return 'https://im.icenter.space';
    }
  }
  return '';
}

function deriveWsBaseUrl(apiBaseUrl, currentWsBaseUrl = '') {
  if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_WS_URL) {
    return trimTrailingSlash(process.env.NEXT_PUBLIC_WS_URL);
  }
  if (currentWsBaseUrl) {
    return trimTrailingSlash(currentWsBaseUrl);
  }

  // 自适应判断正式域名
  if (typeof window !== 'undefined') {
    const isIcenter = window.location?.host?.includes('icenter.space') || window.location?.host?.includes('xwin.live') || window.location?.host?.includes('xwin.live');
    if (isIcenter) {
      return 'wss://im.icenter.space/ws';
    }
  }

  if (!isAbsoluteUrl(apiBaseUrl)) {
    return '';
  }

  return trimTrailingSlash(apiBaseUrl).replace(/^http/i, 'ws');
}


// ============ QBit Chain 网络配置 ============

/**
 * QBit Chain 规范程序地址（重新创世后与 Solana 规范地址对齐）
 * 单一来源，供所有 SDK / web 引用，杜绝散落硬编码。
 * 与 qbit_webs/qbit-chain.config.json 及 configs/qbit_program_registry.json 保持一致。
 */
export const CANONICAL_PROGRAMS = {
  system: '11111111111111111111111111111111',
  token: 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
  token2022: 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb',
  associatedToken: 'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL',
  memo: 'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr',
  tokenMetadata: 'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s',
  mplCore: 'CoREENxT6tW1HoK8ypY1SxRMZTcVPm7R94rH4PZNhX7d',
  raydiumCpmm: 'CPMMoo8L3F4NbTegBCKVNunggL7H1ZpdTHKxQB5qKP1C',
  raydiumCpSwap: 'CPMMoo8L3F4NbTegBCKVNunggL7H1ZpdTHKxQB5qKP1C', // 别名(向后兼容)
  raydiumAmmV4: '675kPX9MHTjS2zt1qfr1NYHuzeLXfQM9H24wFSUt1Mp8',
  raydiumClmm: 'CAMMCzo5YL8w4VFF8KVHrK22GGUsp5VTaW7grrKgrWqK',
  squadsV4: 'SQDS4ep65T869zMMBKyuUq6aD6EgTu8psMjkvj52pCf',
  orcaWhirlpool: 'whirLbMiicVdio4qvUfM5KAg6Ct8VwpYzGff3uctyCc',
  openbookV2: 'opnb2LAfJYbRMAHHvqjCwQxanZn7ReEHp1k81EohpZb',
  mplBubblegum: 'BGUMAp9Gq7iTEuizy4pqaxsTyUCBK68MDfK752saRPUY',
  mplCandyGuard: 'Guard1JwRhJkVH6XZhzoYxeBVQe872VH6QggF4BWmS9g',
};

/**
 * QBit 代币 mint（非 program_id）。
 * ⚠️ XWIN 当前为旧链 mint，重新创世后需在规范 TokenkegQ 下重发并回填新 mint。
 */
export const CANONICAL_TOKENS = {
  wQBT: { mint: 'So11111111111111111111111111111111111111112', decimals: 9, symbol: 'wQBT', name: 'Wrapped QBT' },
  XWIN: { mint: 'Gw3veigZZ9FYfhixrQkD8hLUGUGq88AbTgnu7Ay3WwBa', decimals: 6, symbol: 'XWIN', name: 'XWIN', status: 'pending_reissue_on_new_genesis' },
};

/**
 * QBit Chain 网络端点配置
 * 基于 Solana 改编，RPC 接口完全兼容
 */
export const CHAIN_NETWORKS = {
  mainnet: {
    name: 'QBit Mainnet',
    rpcEndpoint: 'https://api.mainnet.qbitchain.io/',
    wsEndpoint: 'wss://api.mainnet.qbitchain.io/',
    explorerUrl: 'https://explorer.mg.qbitchain.io',
    genesisHash: 'ABQCTh13HE4NvKGCjppZn5KajNpm35yqQMUXosVacAyR',
    nativeToken: { symbol: 'QBT', decimals: 9, name: 'QBit Token' },
    programs: { ...CANONICAL_PROGRAMS },
    tokens: { ...CANONICAL_TOKENS },
  },
  testnet: {
    name: 'QBit Testnet',
    rpcEndpoint: 'https://api.testnet.qbitchain.io/',
    wsEndpoint: 'wss://api.testnet.qbitchain.io/',
    explorerUrl: 'https://explorer.testnet.qbitchain.io',
    genesisHash: '',
    nativeToken: { symbol: 'QBT', decimals: 9, name: 'QBit Token (Test)' },
    programs: { ...CANONICAL_PROGRAMS },
    tokens: { ...CANONICAL_TOKENS },
  },
  devnet: {
    name: 'QBit Devnet',
    rpcEndpoint: 'https://api.devnet.qbitchain.io/',
    wsEndpoint: 'wss://api.devnet.qbitchain.io/',
    explorerUrl: 'https://explorer.devnet.qbitchain.io',
    genesisHash: '',
    nativeToken: { symbol: 'QBT', decimals: 9, name: 'QBit Token (Dev)' },
    programs: { ...CANONICAL_PROGRAMS },
    tokens: { ...CANONICAL_TOKENS },
  },
};

// ============ 环境预设 ============

/**
 * 环境预设配置
 * 快速切换不同环境的配置
 */
export const ENVIRONMENT_PRESETS = {
  development: {
    apiBaseUrl: '/api/v1',
    apiFullUrl: '',
    wsBaseUrl: '',
    chainNetwork: 'devnet',
    chainEndpoint: CHAIN_NETWORKS.devnet.rpcEndpoint,
    chainWsEndpoint: CHAIN_NETWORKS.devnet.wsEndpoint,
    explorerUrl: CHAIN_NETWORKS.devnet.explorerUrl,
    ipfsGateway: 'https://ipfs.io/ipfs',
    debug: true,
    logLevel: 'debug',
    merchantUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_MERCHANT_URL) || 'http://127.0.0.1:3501',
    adminUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ADMIN_URL) || 'http://127.0.0.1:3400',
    payUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_PAY_URL) || 'http://127.0.0.1:3502',
    walletUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_WALLET_URL) || 'http://127.0.0.1:3503',
    didApiUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_DID_API_URL) || 'http://127.0.0.1:8102',
    analyticsUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_URL) || 'http://127.0.0.1:8381',
    analyticsApiUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_API_URL) || 'http://127.0.0.1:8381',
    analyticsCollectorUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_COLLECTOR_URL) || 'http://127.0.0.1:8380',
  },
  staging: {
    apiBaseUrl: '/api/v1',
    apiFullUrl: '',
    wsBaseUrl: '',
    chainNetwork: 'testnet',
    chainEndpoint: CHAIN_NETWORKS.testnet.rpcEndpoint,
    chainWsEndpoint: CHAIN_NETWORKS.testnet.wsEndpoint,
    explorerUrl: CHAIN_NETWORKS.testnet.explorerUrl,
    ipfsGateway: 'https://ipfs.io/ipfs',
    debug: true,
    logLevel: 'info',
    merchantUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_MERCHANT_URL) || 'https://merchant.qbitwallet.io',
    adminUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ADMIN_URL) || 'https://admin.qbitwallet.io',
    payUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_PAY_URL) || 'https://pay.qbitwallet.io',
    walletUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_WALLET_URL) || 'https://wallet.qbitwallet.io',
    didApiUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_DID_API_URL) || 'https://did.qbitwallet.io',
    analyticsUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_URL) || 'https://analytics.mydc.space',
    analyticsApiUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_API_URL) || 'https://analytics-api.mydc.space',
    analyticsCollectorUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_COLLECTOR_URL) || 'https://analytics.mydc.space',
  },
  production: {
    apiBaseUrl: '/api/v1',
    apiFullUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_API_URL) || '',
    wsBaseUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_WS_URL) || '',
    chainNetwork: 'mainnet',
    chainEndpoint: CHAIN_NETWORKS.mainnet.rpcEndpoint,
    chainWsEndpoint: CHAIN_NETWORKS.mainnet.wsEndpoint,
    explorerUrl: CHAIN_NETWORKS.mainnet.explorerUrl,
    ipfsGateway: 'https://ipfs.io/ipfs',
    debug: false,
    logLevel: 'error',
    merchantUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_MERCHANT_URL) || 'https://merchant.qbitwallet.io',
    adminUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ADMIN_URL) || 'https://admin.qbitwallet.io',
    payUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_PAY_URL) || 'https://pay.qbitwallet.io',
    walletUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_WALLET_URL) || 'https://wallet.qbitwallet.io',
    didApiUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_DID_API_URL) || 'https://did.qbitwallet.io',
    analyticsUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_URL) || 'https://analytics.mydc.space',
    analyticsApiUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_API_URL) || 'https://analytics-api.mydc.space',
    analyticsCollectorUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_COLLECTOR_URL) || 'https://analytics.mydc.space',
  },
  icenter: {
    apiBaseUrl: '/api/v1',
    apiFullUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_API_URL) || 'https://im.icenter.space',
    wsBaseUrl: (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_WS_URL) || 'wss://im.icenter.space/ws',
    chainNetwork: 'mainnet',
    chainEndpoint: CHAIN_NETWORKS.mainnet.rpcEndpoint,
    chainWsEndpoint: CHAIN_NETWORKS.mainnet.wsEndpoint,
    explorerUrl: CHAIN_NETWORKS.mainnet.explorerUrl,
    ipfsGateway: 'https://ipfs.io/ipfs',
    debug: false,
    logLevel: 'error',
    merchantUrl: 'https://xwin.live/merchant',
    adminUrl: 'https://xwin.live/admin',
    payUrl: 'https://xwin.live/pay',
    walletUrl: 'https://xwin.live/wallet',
    didApiUrl: 'https://xwin.live/did-api',
    analyticsUrl: 'https://analytics.mydc.space',
    analyticsApiUrl: 'https://analytics-api.mydc.space',
    analyticsCollectorUrl: 'https://analytics.mydc.space',
  },
};

// ============ 配置状态 ============


const getInitialConfig = () => {
  const env = (typeof process !== 'undefined' && process.env?.NODE_ENV) || 'development';
  const preset = ENVIRONMENT_PRESETS[env] || ENVIRONMENT_PRESETS.development;
  return {
    ...DEFAULT_CONFIG,
    ...preset,
    environment: env,
  };
};

let _config = getInitialConfig();
let _listeners = new Set();
let _languageListeners = new Set();  // 语言变更监听器

// ============ 核心函数 ============

/**
 * 获取当前配置
 * @returns {object} 当前配置对象
 */
export function getConfig() {
  return { ..._config };
}

/**
 * 根据服务名称获取端点 URL
 * @param {string} name - 服务名称，如 'ANALYTICS_COLLECTOR' | 'ANALYTICS_API' | 'DID_API'
 * @returns {string} 端点 URL
 */
export function getEndpoint(name) {
  const config = getConfig();
  switch (name) {
    case 'ANALYTICS_COLLECTOR':
      return config.analyticsCollectorUrl || 'https://analytics.mydc.space';
    case 'ANALYTICS_API':
      return config.analyticsApiUrl || 'https://analytics-api.mydc.space';
    case 'DID_API':
      return config.didApiUrl || 'https://did.qbitwallet.io';
    default:
      return '';
  }
}

/**
 * 初始化 SDK 配置
 * 应在应用启动时调用一次
 * 
 * @param {object} options - 配置选项
 * @param {string} options.apiBaseUrl - API base URL（如 '/api/v1' 或完整 URL）
 * @param {number} options.timeout - 请求Timeout（毫秒）
 * @param {boolean} options.debug - 是否开启Debug mode
 * @param {string} options.environment - 环境标识
 */
export function initConfig(options = {}) {
  _config = {
    ..._config,
    ...options,
  };

  if (Object.prototype.hasOwnProperty.call(options, 'apiBaseUrl')) {
    _config.apiFullUrl = deriveApiFullUrl(_config.apiBaseUrl);
    if (!Object.prototype.hasOwnProperty.call(options, 'wsBaseUrl')) {
      _config.wsBaseUrl = deriveWsBaseUrl(_config.apiBaseUrl, '');
    }
  }

  if (Object.prototype.hasOwnProperty.call(options, 'wsBaseUrl')) {
    _config.wsBaseUrl = deriveWsBaseUrl(_config.apiBaseUrl, options.wsBaseUrl);
  }

  // 通知所有监听者
  _notifyListeners();

  if (_config.debug) {
    console.log('[SDK Config] 配置已初始化:', _config);
  }

  return _config;
}

/**
 * 设置 API base URL
 * 便捷方法，用于动态切换 API 地址
 * 
 * @param {string} url - 新的 API URL
 */
export function setApiBaseUrl(url) {
  _config.apiBaseUrl = url;
  _config.apiFullUrl = deriveApiFullUrl(url);
  _config.wsBaseUrl = deriveWsBaseUrl(url, '');
  _notifyListeners();

  if (_config.debug) {
    console.log('[SDK Config] API URL 已更新:', url);
  }
}

/**
 * 设置链网络
 * 便捷方法，切换 mainnet/testnet/devnet
 * 
 * @param {string} network - 网络名称 ('mainnet' | 'testnet' | 'devnet')
 */
export function setChainNetwork(network) {
  const endpoints = CHAIN_NETWORKS[network];
  if (!endpoints) {
    console.warn(`[SDK Config] 未知链网络: ${network}，支持: mainnet, testnet, devnet`);
    return;
  }
  _config.chainNetwork = network;
  _config.chainEndpoint = endpoints.rpcEndpoint;
  _config.chainWsEndpoint = endpoints.wsEndpoint || '';
  if (endpoints.explorerUrl) {
    _config.explorerUrl = endpoints.explorerUrl;
  }
  _notifyListeners();

  if (_config.debug) {
    console.log(`[SDK Config] 链网络已切换: ${network} -> ${endpoints.rpcEndpoint}`);
  }
}

/**
 * 获取当前链配置
 * @returns {object} 链配置对象
 */
export function getChainConfig() {
  return {
    network: _config.chainNetwork,
    endpoint: _config.chainEndpoint,
    wsEndpoint: _config.chainWsEndpoint,
    commitment: _config.chainCommitment,
    timeoutMs: _config.chainTimeoutMs,
    ...CHAIN_NETWORKS[_config.chainNetwork],
  };
}

/**
 * 设置 WebSocket URL
 * 
 * @param {string} url - 新的 WebSocket URL
 */
export function setWsBaseUrl(url) {
  _config.wsBaseUrl = deriveWsBaseUrl(_config.apiBaseUrl, url);
  _notifyListeners();
}

/**
 * 设置Debug mode
 * 
 * @param {boolean} debug - 是否开启调试
 */
export function setDebug(debug) {
  _config.debug = debug;
  _notifyListeners();
}

/**
 * 重置为默认配置
 */
export function resetConfig() {
  _config = getInitialConfig();
  _notifyListeners();
}

// ============ 配置监听 ============

/**
 * 订阅配置变更
 * 
 * @param {function} listener - 监听回调
 * @returns {function} 取消订阅函数
 */
export function subscribeConfig(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

function _notifyListeners() {
  const config = getConfig();
  _listeners.forEach(listener => {
    try {
      listener(config);
    } catch (err) {
      console.error('[SDK Config] 监听器执行错误:', err);
    }
  });
}

// CANONICAL_PROGRAMS, CANONICAL_TOKENS, CHAIN_NETWORKS, ENVIRONMENT_PRESETS relocated to top of file

/**
 * 使用环境预设初始化配置
 * 
 * @param {string} env - 环境名称 ('development' | 'staging' | 'production')
 * @param {object} overrides - 额外覆盖配置
 */
export function initWithEnvironment(env, overrides = {}) {
  const preset = ENVIRONMENT_PRESETS[env];
  if (!preset) {
    console.warn(`[SDK Config] 未知环境: ${env}, 使用默认配置`);
    return initConfig(overrides);
  }

  return initConfig({
    ...preset,
    environment: env,
    ...overrides,
  });
}

// ============ React Hook ============

import { useState, useEffect, useCallback } from 'react';

/**
 * React Hook: 获取和管理 SDK 配置
 * 
 * @example
 * const { config, setApiBaseUrl, initConfig } = useSDKConfig();
 * 
 * // 读取配置
 * console.log(config.apiBaseUrl);
 * 
 * // 动态修改 API URL
 * setApiBaseUrl('https://new-api.example.com');
 */
export function useSDKConfig() {
  const [config, setConfigState] = useState(getConfig);

  useEffect(() => {
    const unsubscribe = subscribeConfig(setConfigState);
    return unsubscribe;
  }, []);

  const updateConfig = useCallback((options) => {
    initConfig(options);
  }, []);

  const updateApiUrl = useCallback((url) => {
    setApiBaseUrl(url);
  }, []);

  const updateWsUrl = useCallback((url) => {
    setWsBaseUrl(url);
  }, []);

  const reset = useCallback(() => {
    resetConfig();
  }, []);

  return {
    config,
    initConfig: updateConfig,
    setApiBaseUrl: updateApiUrl,
    setWsBaseUrl: updateWsUrl,
    resetConfig: reset,
    initWithEnvironment,
  };
}

/**
 * React Hook: 获取和管理 QBit Chain 配置
 * 
 * @example
 * const { chainConfig, setNetwork } = useChainConfig();
 * console.log(chainConfig.endpoint);
 * setNetwork('testnet');
 */
export function useChainConfig() {
  const [chainConfig, setChainState] = useState(getChainConfig);

  useEffect(() => {
    const unsubscribe = subscribeConfig(() => {
      setChainState(getChainConfig());
    });
    return unsubscribe;
  }, []);

  const setNetwork = useCallback((network) => {
    setChainNetwork(network);
  }, []);

  return {
    chainConfig,
    setNetwork,
    networks: CHAIN_NETWORKS,
  };
}

// ============ Request Utilities ============

/**
 * 构建完整的 API URL
 * 
 * @param {string} endpoint - API 端点（如 '/auth/login'）
 * @param {boolean} useFullUrl - 是否使用完整 URL
 * @returns {string} 完整的 URL
 */
export function buildApiUrl(endpoint, useFullUrl = false) {
  const baseUrl = useFullUrl ? _config.apiFullUrl : _config.apiBaseUrl;
  if (!baseUrl) {
    return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  }
  if (isAbsoluteUrl(endpoint)) {
    return endpoint;
  }
  // 确保不会出现双斜杠
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const cleanBase = trimTrailingSlash(baseUrl);
  return `${cleanBase}${cleanEndpoint}`;
}

/**
 * 获取认证 Token
 * 
 * @returns {string|null} Token 或 null
 */
export function getToken() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(_config.tokenStorageKey);
}

/**
 * 获取刷新 Token
 * 
 * @returns {string|null} Refresh Token 或 null
 */
export function getRefreshToken() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(_config.refreshTokenStorageKey);
}

/**
 * 保存认证 Token
 * 
 * @param {string} accessToken - Access Token
 * @param {string} refreshToken - Refresh Token（可选）
 */
export function saveTokens(accessToken, refreshToken) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(_config.tokenStorageKey, accessToken);
  if (refreshToken) {
    localStorage.setItem(_config.refreshTokenStorageKey, refreshToken);
  }
}

/**
 * 清除所有 Token
 */
export function clearTokens() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(_config.tokenStorageKey);
  localStorage.removeItem(_config.refreshTokenStorageKey);
  localStorage.removeItem(_config.currentSessionKey);
}

/**
 * 检查是否已认证
 * 
 * @returns {boolean} 是否有有效 Token
 */
export function isAuthenticated() {
  return !!getToken();
}

// ============ Logger ============

const LOG_LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };

/**
 * SDK 日志工具
 * 根据配置的日志级别输出日志
 */
export const logger = {
  debug: (...args) => {
    if (_config.debug && LOG_LEVELS[_config.logLevel] <= LOG_LEVELS.debug) {
      console.debug('[SDK]', ...args);
    }
  },
  info: (...args) => {
    if (_config.debug && LOG_LEVELS[_config.logLevel] <= LOG_LEVELS.info) {
      console.info('[SDK]', ...args);
    }
  },
  warn: (...args) => {
    if (LOG_LEVELS[_config.logLevel] <= LOG_LEVELS.warn) {
      console.warn('[SDK]', ...args);
    }
  },
  error: (...args) => {
    console.error('[SDK]', ...args);
  },
};

// ============ Language Management ============

/**
 * 获取当前语言
 * 优先从 localStorage 读取，否则使用默认配置
 * 
 * @returns {string} 当前语言代码
 */
export function getLanguage() {
  // SSR 安全检查
  if (typeof window === 'undefined') {
    return _config.language;
  }

  // 尝试从 localStorage 读取
  const stored = localStorage.getItem(_config.languageStorageKey);
  if (stored && _config.supportedLanguages.includes(stored)) {
    return stored;
  }

  // 尝试从浏览器获取
  const browserLang = navigator.language?.split('-')[0];
  if (browserLang && _config.supportedLanguages.includes(browserLang)) {
    return browserLang;
  }

  return _config.language;
}

/**
 * 设置全局语言
 * 会通知所有订阅的 SDK 和组件
 * 
 * @param {string} lang - 语言代码 ('zh' | 'en' | 'ja' | 'ko')
 * @returns {boolean} 是否设置成功
 */
export function setLanguage(lang) {
  // 验证语言代码
  if (!_config.supportedLanguages.includes(lang)) {
    logger.warn(`不Supported Languages: ${lang}, Supported Languages: ${_config.supportedLanguages.join(', ')}`);
    return false;
  }

  const previousLang = _config.language;
  _config.language = lang;

  // 持久化到 localStorage
  if (typeof window !== 'undefined') {
    localStorage.setItem(_config.languageStorageKey, lang);

    // 触发全局 CustomEvent，让不直接依赖此模块的 SDK 也能收到通知
    try {
      const event = new CustomEvent('qbit-did:language-change', {
        detail: { language: lang, oldLanguage: previousLang }
      });
      window.dispatchEvent(event);
    } catch (e) {
      // 忽略事件触发错误
    }
  }

  // 通知所有语言监听器
  _notifyLanguageListeners(lang, previousLang);

  if (_config.debug) {
    console.log(`[SDK Config] 语言已切换: ${previousLang} -> ${lang}`);
  }

  return true;
}

/**
 * 初始化语言（应用启动时调用）
 * 从 localStorage 或浏览器偏好读取
 */
export function initLanguage() {
  const detectedLang = getLanguage();
  _config.language = detectedLang;

  if (_config.debug) {
    console.log(`[SDK Config] 语言已初始化: ${detectedLang}`);
  }

  return detectedLang;
}

/**
 * 订阅语言变更
 * 
 * @param {function} listener - 回调函数 (newLang, oldLang) => void
 * @returns {function} 取消订阅函数
 */
export function subscribeLanguage(listener) {
  _languageListeners.add(listener);
  return () => _languageListeners.delete(listener);
}

function _notifyLanguageListeners(newLang, oldLang) {
  _languageListeners.forEach(listener => {
    try {
      listener(newLang, oldLang);
    } catch (err) {
      console.error('[SDK Config] 语言监听器执行错误:', err);
    }
  });
}

/**
 * React Hook: 获取和管理语言
 * 
 * @example
 * const { language, setLanguage, supportedLanguages } = useLanguage();
 * 
 * // 切换语言
 * setLanguage('en');
 */
export function useLanguage() {
  const [language, setLangState] = useState(getLanguage);

  useEffect(() => {
    // 初始化语言
    const currentLang = getLanguage();
    setLangState(currentLang);

    // 订阅语言变更
    const unsubscribe = subscribeLanguage((newLang) => {
      setLangState(newLang);
    });

    return unsubscribe;
  }, []);

  const updateLanguage = useCallback((lang) => {
    setLanguage(lang);
  }, []);

  return {
    language,
    setLanguage: updateLanguage,
    supportedLanguages: _config.supportedLanguages,
    fallbackLanguage: _config.fallbackLanguage,
  };
}

/**
 * Resolves the analytics site_id dynamically based on current host or environment variable
 * with fallbacks for local dev environments.
 * 
 * @param {string} host - window.location.host
 * @param {string} legacyAppId - fallback identifier if no host match is found
 * @returns {string} resolved site_id
 */
export function resolveSiteId(host, legacyAppId) {
  if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ANALYTICS_SITE_ID) {
    return process.env.NEXT_PUBLIC_ANALYTICS_SITE_ID;
  }
  if (!host) return legacyAppId;
  const h = host.toLowerCase();

  // 1. PlayXWin Casino
  if (h.includes("playxwin")) {
    return "playxwin_prod";
  }
  // 2. XWin Official Center
  if (h.includes("xwin.live") || h.includes("icenter.space") || h.includes("xwin.live")) {
    return "xwin_icenter_prod";
  }
  // 3. QBit Wallet
  if (h.includes("wallet.qbitchain") || h.includes("wallet.qbitwallet.io")) {
    return "qbit_wallet_prod";
  }
  // 4. QBit Open Platform (Developer Portal)
  if (h.includes("open.qbitchain") || h.includes("open.qbitwallet.io")) {
    return "1d3908a3b7d3937a";
  }
  // 5. QBit Merchant Platform (Merchant Portal)
  if (h.includes("merchant.qbitchain") || h.includes("merchant.qbitwallet.io") || h.includes("qbit-merchant-portal")) {
    return "qbit_merchant_prod";
  }
  // 6. QBit Pay Platform
  if (h.includes("pay.qbitchain") || h.includes("pay.qbitwallet.io")) {
    return "c73456fadabe8f10";
  }
  // 7. QBit Ad Network
  if (h.includes("ad.qbitchain")) {
    return "f78ac2dc0f8a2e15";
  }
  // 8. Quantabit Analytics Core
  if (h.includes("mydc.space")) {
    return "qbit_mydc_prod";
  }
  // 9. XKol 平台
  if (h.includes("xkol.net") || h.includes("xkol.qbitchain")) {
    return "xkol_prod";
  }
  // 10. AnyAffair 平台
  if (h.includes("anyaffair")) {
    return "anyaffair_prod";
  }
  // 11. XKol Shop 平台
  if (h.includes("xkolshop")) {
    return "xkolshop_prod";
  }
  // 12. AIVtLink 平台
  if (h.includes("aivtlink")) {
    return "aivtlink_prod";
  }
  // 13. StartupOS 平台
  if (h.includes("startup.qbitchain")) {
    return "startup_prod";
  }
  // 14. DreamLadder 平台
  if (h.includes("dreamladder")) {
    return "dreamladder_prod";
  }
  // 15. QCard 平台
  if (h.includes("qcard")) {
    return "qcard_prod";
  }
  // 16. IdeaForge 平台
  if (h.includes("ideaforge")) {
    return "ideaforge_prod";
  }
  // 17. DigiShop 平台
  if (h.includes("digishop")) {
    return "digishop_prod";
  }
  // 18. XCoFound 平台
  if (h.includes("xcofound")) {
    return "xcofound_prod";
  }
  // 19. ImJob 平台
  if (h.includes("imjob")) {
    return "imjob_prod";
  }

  // 127.0.0.1/dev fallback based on legacy appId
  if (!legacyAppId) return "qbit_mydc_prod";
  const appId = legacyAppId.toLowerCase();
  if (appId.includes("playxwin")) {
    return "playxwin_prod";
  }
  if (appId.includes("xwin")) {
    return "xwin_icenter_prod";
  }
  if (appId.includes("wallet-portal") || appId.includes("wallet")) {
    return "qbit_wallet_prod";
  }
  if (appId.includes("merchant-portal") || appId.includes("merchant-pay-portal")) {
    return "qbit_merchant_prod";
  }
  if (appId.includes("developer-portal") || appId.includes("open") || appId.includes("developer")) {
    return "1d3908a3b7d3937a";
  }
  if (appId.includes("pay-portal") || appId.includes("pay_portal") || appId.includes("pay")) {
    return "c73456fadabe8f10";
  }
  if (appId.includes("xkolshop")) {
    return "xkolshop_prod";
  }
  if (appId.includes("xkol")) {
    return "xkol_prod";
  }
  if (appId.includes("anyaffair")) {
    return "anyaffair_prod";
  }
  if (appId.includes("aivtlink")) {
    return "aivtlink_prod";
  }
  if (appId.includes("startup")) {
    return "startup_prod";
  }
  if (appId.includes("dreamladder")) {
    return "dreamladder_prod";
  }
  if (appId.includes("qcard")) {
    return "qcard_prod";
  }
  if (appId.includes("ideaforge")) {
    return "ideaforge_prod";
  }
  if (appId.includes("digishop")) {
    return "digishop_prod";
  }
  if (appId.includes("xcofound")) {
    return "xcofound_prod";
  }
  if (appId.includes("imjob")) {
    return "imjob_prod";
  }

  return "qbit_mydc_prod";
}

// ============ Export BaseApiClient ============

export { BaseApiClient, createApiClient } from './BaseApiClient.js';

// ============ Display Surface (link / modal) ============

import {
  DisplayMode,
  DEFAULT_PAGE_CONTEXT_QUERY_KEYS,
  getPageContext,
  toSearchParams,
  mergeSurfaceParams,
  buildUrl,
  normalizeDisplayMode,
  openSurface,
  closeSurface,
  DisplaySurfaceHelper,
  createDisplaySurfaceHelper,
} from './displaySurface.js';

import {
  openUtilitySurface,
  openFeedbackSurface,
  openHelpSurface,
  openHelpdeskSurface,
  openAISurface,
  openReportSurface,
  openSubscriptionSurface,
  openCheckinSurface,
  openInviteSurface,
  openShareSurface,
  openTipSurface,
  openChatSurface,
  openAIAgentSurface,
  openDonateSurface,
  withPageContext,
} from './utilitySurface.js';

export {
  DisplayMode,
  DEFAULT_PAGE_CONTEXT_QUERY_KEYS,
  getPageContext,
  toSearchParams,
  mergeSurfaceParams,
  buildUrl,
  normalizeDisplayMode,
  openSurface,
  closeSurface,
  DisplaySurfaceHelper,
  createDisplaySurfaceHelper,
  openUtilitySurface,
  openFeedbackSurface,
  openHelpSurface,
  openHelpdeskSurface,
  openAISurface,
  openReportSurface,
  openSubscriptionSurface,
  openCheckinSurface,
  openInviteSurface,
  openShareSurface,
  openTipSurface,
  openChatSurface,
  openAIAgentSurface,
  openDonateSurface,
  withPageContext,
};

// ============ Privacy & Consent ============

export {
  consentManager,
  PrivacyLevel,
  ConsentStatus,
  useConsent,
} from './ConsentManager.js';

export { dataSanitizer } from './DataSanitizer.js';

export { privacyOrchestrator, PrivacyOrchestratorClass } from './PrivacyOrchestrator.js';

// ============ Exports ============

export default {
  getConfig,
  getEndpoint,
  initConfig,
  setApiBaseUrl,
  setWsBaseUrl,
  setDebug,
  resetConfig,
  subscribeConfig,
  initWithEnvironment,
  ENVIRONMENT_PRESETS,
  buildApiUrl,
  getToken,
  getRefreshToken,
  saveTokens,
  clearTokens,
  isAuthenticated,
  logger,
  useSDKConfig,
  // 链配置
  CHAIN_NETWORKS,
  CANONICAL_PROGRAMS,
  CANONICAL_TOKENS,
  setChainNetwork,
  getChainConfig,
  useChainConfig,
  // 语言管理
  getLanguage,
  setLanguage,
  initLanguage,
  subscribeLanguage,
  useLanguage,
  resolveSiteId,
  // 展示面
  DisplayMode,
  getPageContext,
  buildUrl,
  openSurface,
  closeSurface,
  createDisplaySurfaceHelper,
  openUtilitySurface,
  openFeedbackSurface,
  openHelpSurface,
  openHelpdeskSurface,
  openAISurface,
  openReportSurface,
  openSubscriptionSurface,
  openCheckinSurface,
  openInviteSurface,
  openShareSurface,
  openTipSurface,
  openChatSurface,
  openAIAgentSurface,
  openDonateSurface,
  withPageContext,
};
